import { useState, useCallback } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../../Keycloak/useAuthenticatedFetchWithWorkspace";
import { Customer, NewCustomerPayload } from "../../../types/Customer";

const CIVILITY_TO_TITLE: Record<string, string> = { "M.": "1", "Mme": "2" };

type CustomerFormsData = Record<string, Record<string, string>>;

const useAddPerson = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const addPerson = useCallback(
        async (
            payload: NewCustomerPayload,
            formsData: CustomerFormsData = {},
        ): Promise<Customer | null> => {
            if (!isReady) return null;
            setIsLoading(true);
            setError("");
            try {
                const body = {
                    mainInfos: {
                        identifier: payload.identifier || "",
                        title_id: CIVILITY_TO_TITLE[payload.civility || ""] || null,
                        lastname: payload.lastname,
                        firstname: payload.firstname || "",
                        entity_id: Number(payload.store_id) || null,
                        birthdate: payload.birth_date || null,
                    },
                    coordinates: {
                        address1: payload.address1 || "",
                        address2: payload.address2 || "",
                        address3_num: payload.address3_num || "",
                        address3_type: payload.address3_type || "",
                        address3_name: payload.address3_name || "",
                        address4: payload.address4 || "",
                        zip_code: payload.zip_code || "",
                        local_city_name: payload.city || "",
                        local_country_name: payload.country || "",
                        country: payload.country || "",
                        email_address: payload.email || "",
                        cell_phone_number: payload.cellphone || "",
                        phone_number: payload.phone || "",
                    },
                    cnil: {
                        mailCompanySolicitations: payload.solicitation_mail === "1" ? 1 : 0,
                        emailCompanySolicitations: payload.solicitation_email === "1" ? 1 : 0,
                        smsCompanySolicitations: payload.solicitation_sms === "1" ? 1 : 0,
                        mailTrustedSolicitations: payload.solicitation_trusted_mail === "1" ? 1 : 0,
                        emailTrustedSolicitations: payload.solicitation_trusted_email === "1" ? 1 : 0,
                        smsTrustedSolicitations: payload.solicitation_trusted_sms === "1" ? 1 : 0,
                    },
                    forms: formsData,
                };

                const raw = await authFetch("/customer", {
                    method: "POST",
                    body: JSON.stringify(body),
                });

                if (!raw || (typeof raw === "object" && !Array.isArray(raw) && Object.keys(raw as object).length === 0)) {
                    setError("La création du client a échoué. Vérifiez les informations saisies (doublon de carte possible).");
                    return null;
                }

                const result = raw as Record<string, unknown>;
                const pid = result.person_id ?? result.PERSONID;
                if (!pid || pid === 9999999999 || pid === "9999999999") {
                    setError("La création a échoué : aucun identifiant client retourné par l'API.");
                    return null;
                }

                return raw as Customer;
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : "An unknown error occurred");
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, isReady]
    );

    return { isLoading, error, addPerson };
};

export default useAddPerson;
